<template>
  <div class="sms-credits-page">
    <!-- ========================================== -->
    <!-- هدر -->
    <!-- ========================================== -->
    <div class="page-header">
      <div>
        <h2>📱 شارژ پیامک</h2>
        <p class="subtitle">خرید و مدیریت اعتبار پیامک سالن</p>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- کارت اعتبار فعلی -->
    <!-- ========================================== -->
    <div class="current-credit-card">
      <div class="credit-bg-icon">📱</div>
      <div class="credit-info">
        <span class="credit-label">اعتبار فعلی شما</span>
        <span class="credit-value">{{ formatNumber(currentCredit) }} پیامک</span>
        <span class="credit-hint">نرخ هر پیامک: {{ formatPrice(smsRatio) }}</span>
      </div>
      <div :class="['credit-status-badge', creditStatusClass]">
        {{ creditStatusLabel }}
      </div>
    </div>

    <!-- ========================================== -->
    <!-- فرم خرید -->
    <!-- ========================================== -->
    <div class="purchase-card">
      <h3 class="section-title">
        <span class="title-icon">🛒</span>
        خرید شارژ
      </h3>

      <!-- ===== تب انتخاب روش ورود ===== -->
      <div class="input-tabs">
        <button
            :class="['input-tab', { active: inputMode === 'credits' }]"
            @click="inputMode = 'credits'"
        >
          📨 بر اساس تعداد پیامک
        </button>
        <button
            :class="['input-tab', { active: inputMode === 'amount' }]"
            @click="inputMode = 'amount'"
        >
          💰 بر اساس مبلغ (تومان)
        </button>
      </div>

      <!-- ===== ورودی تعداد پیامک ===== -->
      <div v-if="inputMode === 'credits'" class="input-section">
        <label class="input-label">تعداد پیامک مورد نیاز</label>
        <div class="input-wrapper">
          <input
              v-model.number="creditsInput"
              type="number"
              class="form-input"
              placeholder="مثلاً 500"
              min="1"
              @input="onCreditsChange"
          />
          <span class="input-suffix">پیامک</span>
        </div>

        <!-- دکمه‌های پیشنهادی سریع -->
        <div class="quick-amounts">
          <button
              v-for="amount in quickCredits"
              :key="amount"
              :class="['quick-btn', { active: creditsInput === amount }]"
              @click="setCredits(amount)"
          >
            {{ formatNumber(amount) }}
          </button>
        </div>

        <!-- محاسبه مبلغ -->
        <div v-if="creditsInput > 0" class="calc-result">
          <div class="calc-row">
            <span>تعداد پیامک:</span>
            <strong>{{ formatNumber(creditsInput) }} پیامک</strong>
          </div>
          <div class="calc-row">
            <span>نرخ هر پیامک:</span>
            <strong>{{ formatPrice(smsRatio) }}</strong>
          </div>
          <div class="calc-row total">
            <span>مبلغ قابل پرداخت:</span>
            <strong class="calc-total">{{ formatPrice(calculatedAmount) }}</strong>
          </div>
        </div>
      </div>

      <!-- ===== ورودی مبلغ ===== -->
      <div v-else class="input-section">
        <label class="input-label">مبلغ مورد نظر (تومان)</label>
        <div class="input-wrapper">
          <input
              v-model.number="amountInput"
              type="number"
              class="form-input"
              placeholder="مثلاً 250000"
              min="1000"
              step="1000"
              @input="onAmountChange"
          />
          <span class="input-suffix">تومان</span>
        </div>

        <!-- دکمه‌های پیشنهادی سریع -->
        <div class="quick-amounts">
          <button
              v-for="amount in quickAmounts"
              :key="amount"
              :class="['quick-btn', { active: amountInput === amount }]"
              @click="setAmount(amount)"
          >
            {{ formatNumber(amount) }}
          </button>
        </div>

        <!-- محاسبه تعداد پیامک -->
        <div v-if="amountInput > 0" class="calc-result">
          <div class="calc-row">
            <span>مبلغ وارد شده:</span>
            <strong>{{ formatPrice(amountInput) }}</strong>
          </div>
          <div class="calc-row">
            <span>نرخ هر پیامک:</span>
            <strong>{{ formatPrice(smsRatio) }}</strong>
          </div>
          <div class="calc-row total">
            <span>تعداد پیامک:</span>
            <strong class="calc-total">{{ formatNumber(calculatedCredits) }} پیامک</strong>
          </div>
        </div>
      </div>

      <!-- ===== دکمه خرید ===== -->
      <button
          class="btn-purchase"
          :disabled="!canPurchase || processing"
          @click="purchase"
      >
        <span v-if="processing">⏳ در حال انتقال به درگاه...</span>
        <span v-else-if="!canPurchase">لطفاً مقدار معتبر وارد کنید</span>
        <span v-else>
          💳 پرداخت {{ formatPrice(finalAmount) }} و خرید
        </span>
      </button>
    </div>

    <!-- ========================================== -->
    <!-- تاریخچه خریدها -->
    <!-- ========================================== -->
    <h3 class="section-title">
      <span class="title-icon">📋</span>
      تاریخچه خریدها
    </h3>

    <div v-if="historyLoading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else-if="history.length === 0" class="empty-state">
      <div class="empty-icon">📭</div>
      <h4>هنوز خریدی انجام نشده است</h4>
      <p>با وارد کردن تعداد پیامک یا مبلغ، اولین شارژ خود را انجام دهید.</p>
    </div>

    <div v-else class="history-list">
      <div v-for="item in history" :key="item.id" class="history-item">
        <div class="history-icon">📨</div>
        <div class="history-main">
          <div class="history-credits">{{ formatNumber(item.credits) }} پیامک</div>
          <div class="history-date">{{ get_date_time(item.created_at) }}</div>
        </div>
        <div class="history-amount">{{ formatPrice(item.amount) }}</div>
        <div :class="['badge', statusClass(item.status)]">
          {{ item.status_label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SmsCreditsPage',

  data() {
    return {
      historyLoading: false,
      processing: false,
      currentCredit: 0,
      smsRatio: 500,
      history: [],

      // ===== ورودی‌ها =====
      inputMode: 'credits', // 'credits' | 'amount'
      creditsInput: null,
      amountInput: null,

      // ===== پیشنهادهای سریع =====
      quickCredits: [100, 500, 1000, 5000],
      quickAmounts: [50000, 100000, 250000, 500000]
    };
  },

  computed: {
    // ==========================================
    // محاسبه مبلغ از تعداد پیامک
    // ==========================================
    calculatedAmount() {
      if (!this.creditsInput || this.creditsInput <= 0) return 0;
      return this.creditsInput * this.smsRatio;
    },

    // ==========================================
    // محاسبه تعداد پیامک از مبلغ
    // ==========================================
    calculatedCredits() {
      if (!this.amountInput || this.amountInput <= 0) return 0;
      return Math.floor(this.amountInput / this.smsRatio);
    },

    // ==========================================
    // مبلغ نهایی
    // ==========================================
    finalAmount() {
      if (this.inputMode === 'credits') {
        return this.calculatedAmount;
      }
      return this.amountInput || 0;
    },

    // ==========================================
    // تعداد پیامک نهایی
    // ==========================================
    finalCredits() {
      if (this.inputMode === 'credits') {
        return this.creditsInput || 0;
      }
      return this.calculatedCredits;
    },

    // ==========================================
    // آیا می‌توان خرید کرد؟
    // ==========================================
    canPurchase() {
      if (!this.smsRatio || this.smsRatio <= 0) return false;
      if (this.finalCredits < 1) return false;
      if (this.finalAmount < 1000) return false;
      return true;
    },

    // ==========================================
    // وضعیت اعتبار
    // ==========================================
    creditStatusClass() {
      if (this.currentCredit <= 0) return 'badge-danger';
      if (this.currentCredit < 100) return 'badge-warning';
      return 'badge-success';
    },

    creditStatusLabel() {
      if (this.currentCredit <= 0) return 'اعتبار تمام شده';
      if (this.currentCredit < 100) return 'اعتبار کم';
      return 'فعال';
    }
  },

  mounted() {
    this.loadData();
    this.loadHistory();
  },

  methods: {
    // ==========================================
    // بارگذاری اطلاعات
    // ==========================================
    async loadData() {
      try {
        const { data } = await axios.get('/api/admin/sms-credits/packages', {});
        this.currentCredit = data.current_credit || 0;
        this.smsRatio = data.sms_ratio || 500;
      } catch (error) {
        console.error(error);
        this.showError('خطا در بارگذاری اطلاعات');
      }
    },

    // ==========================================
    // بارگذاری تاریخچه
    // ==========================================
    async loadHistory() {
      this.historyLoading = true;
      try {
        const { data } = await axios.get('/api/admin/sms-credits/history');
        this.history = data.data || [];
      } catch (error) {
        console.error(error);
      } finally {
        this.historyLoading = false;
      }
    },

    // ==========================================
    // تغییر حالت ورودی
    // ==========================================
    onCreditsChange() {
      // فقط اعداد صحیح
      if (this.creditsInput) {
        this.creditsInput = Math.max(1, Math.floor(this.creditsInput));
      }
    },

    onAmountChange() {
      if (this.amountInput) {
        this.amountInput = Math.max(1000, Math.floor(this.amountInput));
      }
    },

    // ==========================================
    // تنظیم سریع
    // ==========================================
    setCredits(amount) {
      this.creditsInput = amount;
    },

    setAmount(amount) {
      this.amountInput = amount;
    },

    // ==========================================
    // خرید
    // ==========================================
    async purchase() {
      if (!this.canPurchase) return;

      this.processing = true;
      try {
        const { data } = await axios.post('/api/admin/sms-credits/purchase', {
          credits: this.finalCredits
        });

        window.location.href = data.payment_url;

      } catch (error) {
        const msg = error.response?.data?.message || 'خطا در ایجاد پرداخت';
        this.showError(msg);
      } finally {
        this.processing = false;
      }
    },

    // ==========================================
    // نمایش خطا
    // ==========================================
    showError(msg) {
      if (this.$toast) {
        this.$toast.error(msg);
      } else {
        alert(msg);
      }
    },

    // ==========================================
    // فرمت‌دهی
    // ==========================================
    formatNumber(value) {
      if (value === null || value === undefined) return '۰';
      return new Intl.NumberFormat('fa-IR').format(value);
    },

    formatPrice(value) {
      if (!value) return '۰ تومان';
      return new Intl.NumberFormat('fa-IR').format(value) + ' تومان';
    },


    statusClass(status) {
      const map = {
        pending: 'badge-warning',
        paid: 'badge-success',
        failed: 'badge-danger'
      };
      return map[status] || 'badge-gray';
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* صفحه */
/* ========================================== */
.sms-credits-page {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
  direction: rtl;
  box-sizing: border-box;
}

/* ========================================== */
/* هدر */
/* ========================================== */
.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 700;
}

.subtitle {
  margin: 4px 0 0 0;
  color: #909399;
  font-size: 14px;
}

/* ========================================== */
/* کارت اعتبار فعلی */
/* ========================================== */
.current-credit-card {
  position: relative;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 16px;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 20px;
  color: white;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(79, 172, 254, 0.35);
}

.credit-bg-icon {
  position: absolute;
  top: 50%;
  left: -10px;
  transform: translateY(-50%);
  font-size: 140px;
  opacity: 0.12;
  pointer-events: none;
  line-height: 1;
}

.credit-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1;
}

.credit-label {
  font-size: 13px;
  opacity: 0.9;
  font-weight: 500;
}

.credit-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.credit-hint {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 4px;
}

.credit-status-badge {
  z-index: 1;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

/* ========================================== */
/* عنوان بخش */
/* ========================================== */
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2c3e50;
  font-size: 17px;
  font-weight: 700;
  margin: 28px 0 16px 0;
}

.title-icon {
  font-size: 20px;
}

/* ========================================== */
/* کارت خرید */
/* ========================================== */
.purchase-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

/* ========================================== */
/* تب انتخاب روش */
/* ========================================== */
.input-tabs {
  display: flex;
  gap: 8px;
  background: #f5f7fa;
  padding: 6px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.input-tab {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  transition: all 0.25s;
}

.input-tab:hover {
  background: #e8f0fe;
}

.input-tab.active {
  background: white;
  color: #4facfe;
  box-shadow: 0 2px 8px rgba(79, 172, 254, 0.2);
}

/* ========================================== */
/* بخش ورودی */
/* ========================================== */
.input-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.input-label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  padding: 14px 70px 14px 16px;
  border: 2px solid #e4e7ed;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  background: white;
  transition: all 0.25s;
  box-sizing: border-box;
  direction: ltr;
  text-align: right;
}

.form-input:focus {
  outline: none;
  border-color: #4facfe;
  box-shadow: 0 0 0 4px rgba(79, 172, 254, 0.1);
}

.form-input::placeholder {
  color: #c0c4cc;
  font-weight: 400;
}

.input-suffix {
  position: absolute;
  left: 16px;
  font-size: 13px;
  font-weight: 600;
  color: #909399;
  pointer-events: none;
}

/* ========================================== */
/* دکمه‌های پیشنهادی سریع */
/* ========================================== */
.quick-amounts {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-btn {
  padding: 8px 18px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: #ecf5ff;
  border-color: #4facfe;
  color: #4facfe;
}

.quick-btn.active {
  background: #4facfe;
  border-color: #4facfe;
  color: white;
  box-shadow: 0 2px 8px rgba(79, 172, 254, 0.35);
}

/* ========================================== */
/* نتیجه محاسبه */
/* ========================================== */
.calc-result {
  background: linear-gradient(135deg, #f0faff 0%, #e6f7ff 100%);
  border: 1px solid #b3e5fc;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 4px;
}

.calc-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
  color: #606266;
}

.calc-row strong {
  color: #2c3e50;
  font-weight: 700;
}

.calc-row.total {
  border-top: 1px dashed #b3e5fc;
  padding-top: 12px;
  margin-top: 8px;
  font-size: 15px;
}

.calc-total {
  font-size: 20px;
  color: #4facfe !important;
  font-weight: 800 !important;
}

/* ========================================== */
/* دکمه خرید */
/* ========================================== */
.btn-purchase {
  width: 100%;
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(79, 172, 254, 0.4);
}

.btn-purchase:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(79, 172, 254, 0.5);
}

.btn-purchase:active:not(:disabled) {
  transform: translateY(0);
}

.btn-purchase:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
  box-shadow: none;
}

/* ========================================== */
/* تاریخچه */
/* ========================================== */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 14px;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 14px 18px;
  transition: all 0.2s;
}

.history-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.history-icon {
  width: 44px;
  height: 44px;
  background: #f0faff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.history-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.history-credits {
  font-size: 14px;
  font-weight: 700;
  color: #2c3e50;
}

.history-date {
  font-size: 12px;
  color: #909399;
}

.history-amount {
  font-size: 14px;
  font-weight: 700;
  color: #4facfe;
  white-space: nowrap;
}

/* ========================================== */
/* بج‌ها */
/* ========================================== */
.badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-success { background: #f0f9eb; color: #67c23a; }
.badge-warning { background: #fdf6ec; color: #e6a23c; }
.badge-danger { background: #fef0f0; color: #f56c6c; }
.badge-gray { background: #f4f4f5; color: #909399; }

/* ========================================== */
/* بارگذاری */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4facfe;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========================================== */
/* خالی */
/* ========================================== */
.empty-state {
  text-align: center;
  padding: 48px 20px;
  background: white;
  border-radius: 12px;
  border: 1px dashed #e4e7ed;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.empty-state h4 {
  color: #2c3e50;
  font-size: 15px;
  margin: 0 0 6px 0;
}

.empty-state p {
  color: #909399;
  font-size: 13px;
  margin: 0;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .sms-credits-page {
    padding: 12px;
  }

  .page-header h2 {
    font-size: 20px;
  }

  .current-credit-card {
    padding: 18px 20px;
    gap: 14px;
    flex-wrap: wrap;
  }

  .credit-value {
    font-size: 24px;
  }

  .credit-bg-icon {
    font-size: 100px;
  }

  .purchase-card {
    padding: 18px;
  }

  .input-tabs {
    flex-direction: column;
  }

  .form-input {
    font-size: 15px;
    padding: 12px 60px 12px 14px;
  }

  .quick-btn {
    padding: 6px 14px;
    font-size: 12px;
  }

  .calc-total {
    font-size: 18px !important;
  }

  .btn-purchase {
    font-size: 14px;
    padding: 14px;
  }

  .history-item {
    flex-wrap: wrap;
    padding: 12px 14px;
  }

  .history-amount {
    width: 100%;
    text-align: left;
    margin-top: 4px;
  }
}

@media (max-width: 480px) {
  .sms-credits-page {
    padding: 8px;
  }

  .credit-value {
    font-size: 22px;
  }

  .credit-status-badge {
    font-size: 11px;
    padding: 4px 12px;
  }

  .section-title {
    font-size: 15px;
    margin: 20px 0 12px 0;
  }

  .form-input {
    font-size: 14px;
  }

  .quick-amounts {
    gap: 6px;
  }

  .quick-btn {
    padding: 6px 12px;
    font-size: 11px;
  }

  .calc-row {
    font-size: 13px;
  }

  .calc-total {
    font-size: 16px !important;
  }
}
</style>